import {
  Banner,
  Button,
  Divider,
  Grid,
  GridColumn,
  Modal,
  Spinner
} from "@modernatx/ui-kit-react";
import React, { useRef, useState } from "react";

import { SchedulePayModalContent } from "@/components/Payments/AutoPayModal/SchedulePayModalContent";
import { useUserInfoContext } from "@/contexts";
import {
  ExistingScheduledPayment,
  useScheduledPaymentSetup
} from "@/hooks/useScheduledPaymentSetup";
import { useSchedulePayment } from "@/hooks/useSchedulePayment";
import {
  AutomaticPaymentType,
  InvoiceDataType,
  ScheduledPaymentDetails
} from "@/types";
import { AddNewPaymentMethod, PaymentMethod } from "@/types/PaymentMethod";
import { ErrorTypes } from "@/utils/Errors/errorMessages";
import { getErrorMessage } from "@/utils/Errors/getErrorMessage";

export interface SchedulePayModalProps {
  isVisible: boolean;
  setVisible: (visible: boolean) => void;
  invoiceData?: InvoiceDataType;
  onScheduleSuccess?: (
    schedule: ScheduledPaymentDetails,
    message?: string
  ) => void;
  paymentSchedule?: ScheduledPaymentDetails | null;
  userId?: string;
}

export enum PaymentScheduleOptions {
  OnDueDate = "ON_DUE_DATE",
  OneDayBefore = "ONE_DAY_BEFORE",
  TwoDaysBefore = "TWO_DAYS_BEFORE",
  SpecificDate = "SPECIFIC_DATE"
}

export type PaymentSelection =
  | PaymentMethod
  | typeof AddNewPaymentMethod
  | undefined;

export const SchedulePayModal: React.FC<SchedulePayModalProps> = ({
  isVisible,
  setVisible,
  invoiceData,
  onScheduleSuccess,
  paymentSchedule
}) => {
  const {
    createSchedulePayment,
    updateScheduledPayment,
    cancelScheduledPayment
  } = useSchedulePayment();

  const {
    selectedPayment,
    setSelectedPayment,
    showAddPaymentMethod,
    scheduleOption,
    setScheduleOption,
    paymentMethods
  } = useScheduledPaymentSetup(
    paymentSchedule as ExistingScheduledPayment | undefined
  );

  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const isUpdateMode = !!paymentSchedule;

  // currently set to single schedules for scope
  const modalTitle = "Schedule payment";
  const modalButtons = [
    { label: "Cancel", onClick: () => setVisible(false) },
    {
      label: isUpdateMode ? "Update" : "Schedule",
      onClick: () => handleConfirmClick()
    }
  ];
  const paymentFormSubmitRef = useRef<
    (() => Promise<PaymentMethod | undefined>) | undefined
  >(undefined);

  const resolveFinalPaymentMethod = async (): Promise<
    PaymentMethod | undefined
  > => {
    if (
      selectedPayment === AddNewPaymentMethod &&
      paymentFormSubmitRef.current
    ) {
      const newPaymentMethod = await paymentFormSubmitRef.current();
      if (newPaymentMethod) {
        setSelectedPayment(newPaymentMethod);
        return newPaymentMethod;
      }
    }

    if (
      typeof selectedPayment === "object" &&
      selectedPayment?.paymentMethodId
    ) {
      return selectedPayment;
    }

    return undefined;
  };

  const { userInfo } = useUserInfoContext() as {
    userInfo: { userId: string; email: string };
  };
  const userId = userInfo?.userId;
  const userEmail = userInfo?.email;

  const handleConfirmClick = async () => {
    try {
      const finalPaymentMethod = await resolveFinalPaymentMethod();
      if (!finalPaymentMethod) return;
      setIsLoading(true);

      const payload = {
        dueDate: invoiceData?.dueDate,
        invoiceId: invoiceData?.invoiceId,
        paymentMethodId: finalPaymentMethod.paymentMethodId,
        scheduleOption: scheduleOption,
        automaticPaymentType: AutomaticPaymentType.SchedulePay,
        soldToOrgId: invoiceData?.soldToOrgId,
        emailAddresses: [userEmail]
      };

      if (isUpdateMode && paymentSchedule?.paymentScheduleId && userId) {
        const updatePayload = {
          ...payload,
          userId
        };
        const result = await updateScheduledPayment(
          paymentSchedule.paymentScheduleId,
          updatePayload
        );
        if (result?.status === 200) {
          onScheduleSuccess?.(result.data, "Payment schedule updated!");
          setVisible(false);
        }
      } else {
        const result = await createSchedulePayment(payload);
        if (result?.status === 200) {
          onScheduleSuccess?.(result.data);
          setVisible(false);
        }
      }
    } catch (error: unknown) {
      setErrorMessage(getErrorMessage(ErrorTypes.SchedulePaymentErrors, error));
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteSchedule = async () => {
    if (!paymentSchedule?.paymentScheduleId) return;

    setIsLoading(true);
    try {
      const result = await cancelScheduledPayment(
        paymentSchedule.paymentScheduleId,
        [userEmail]
      );
      if (result?.status === 200) {
        onScheduleSuccess?.(result.data, "Payment schedule canceled!");
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal
      title={modalTitle}
      isOpen={isVisible}
      onDismiss={() => setVisible(false)}
      buttons={modalButtons}
      size="sm"
    >
      {errorMessage && <Banner variant="critical" text={errorMessage} />}
      {paymentMethods.loading || isLoading ? (
        <Grid>
          <GridColumn sx={{ alignItems: "center" }}>
            <Spinner />
          </GridColumn>
        </Grid>
      ) : (
        <>
          <SchedulePayModalContent
            invoiceId={invoiceData?.invoiceId}
            showAddPaymentMethod={showAddPaymentMethod}
            paymentFormSubmitRef={paymentFormSubmitRef}
            selectedPayment={selectedPayment}
            setSelectedPayment={setSelectedPayment}
            paymentScheduleOptions={scheduleOption}
            setPaymentScheduleOptions={setScheduleOption}
            loadingScheduledPayment={isLoading}
            paymentMethods={paymentMethods}
          />
          {paymentSchedule && (
            <Grid
              sx={{
                alignItems: "center",
                textAlign: "center",
                mt: "spacing-7"
              }}
            >
              <GridColumn>
                <Divider sx={{ mb: "spacing-5" }} />
                <Button variant="subtle" onClick={handleDeleteSchedule}>
                  Delete scheduled payment
                </Button>
                <Divider sx={{ mt: "spacing-5" }} />
              </GridColumn>
            </Grid>
          )}
        </>
      )}
    </Modal>
  );
};
